import logger from '@eneco/common/log';
import { OrderProductTypeDto, putEnecoBeXapiSiteApiV1OrdersByOrderIdBasket } from '@eneco/dc-be/client';
import { UseFormSetValue } from 'react-hook-form';
import { ScopedMutator } from 'swr/_internal';

import { OrderManagementFormSchema } from '../types/orderManagementSchema';

const STORAGE_KEY = 'orderManagementState';
const EXPIRY_DAYS = 7;

interface StoredOrderManagementState {
  orderId?: string;
  timestamp: number;
}

export interface OrderManagementStateData {
  orderId?: string;
  timestamp: number;
}

export const saveOrderManagementState = ({ orderId }: { orderId?: string }): void => {
  try {
    const storedValue = localStorage.getItem(STORAGE_KEY);
    const baseData: StoredOrderManagementState = storedValue
      ? (JSON.parse(storedValue) as StoredOrderManagementState)
      : { timestamp: Date.now() };

    const updatedData: StoredOrderManagementState = {
      ...baseData,
      ...(orderId !== undefined && { orderId }),
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
  } catch (error) {
    logger.error(
      'Failed to save order management state to localStorage:',
      error instanceof Error ? error.message : String(error),
    );
  }
};

export const loadOrderManagementState = (): OrderManagementStateData => {
  try {
    const storedValue = localStorage.getItem(STORAGE_KEY);

    if (!storedValue) {
      return { timestamp: Date.now() };
    }

    const stored: StoredOrderManagementState = JSON.parse(storedValue);
    const expiryInMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    if (Date.now() - stored.timestamp > expiryInMs) {
      clearOrderManagementState();
      return { timestamp: Date.now() };
    }

    return {
      orderId: stored.orderId,
      timestamp: stored.timestamp,
    };
  } catch (error) {
    logger.error(
      'Failed to load order management state from localStorage:',
      error instanceof Error ? error.message : String(error),
    );
    return { timestamp: Date.now() };
  }
};

export const clearSelectedProducts = async ({
  orderId,
  selectedServices,
  cacheKey,
  mutate,
}: {
  orderId: string;
  selectedServices: {
    boilerMaintenance: string | string[];
    serviceProposal: string | string[];
  };
  mutate: ScopedMutator;
  cacheKey: string;
}): Promise<void> => {
  try {
    const serviceItems: string[] = [
      selectedServices.boilerMaintenance?.toString(),
      selectedServices.serviceProposal?.toString(),
    ].filter((s): s is string => Boolean(s) && s !== 'none');
    const result = await putEnecoBeXapiSiteApiV1OrdersByOrderIdBasket({
      path: { orderId },
      body: {
        basket: {
          items: serviceItems?.map(si => ({
            technicalProductName: si,
            orderProductType: 'NonCommodity' as OrderProductTypeDto,
          })),
        },
      },
    });
    if (result.response.ok) {
      mutate(cacheKey);
    }
  } catch (error) {
    logger.error(
      'Failed to clear selected products from order basket:',
      error instanceof Error ? error.message : String(error),
    );
    throw error;
  }
};

export const clearSelectedService = async ({
  removeBoilerMaintenance,
  removeServiceProposal,
  orderId,
  selectedProduct,
  selectedServices,
  cacheKey,
  mutate,
  setValue,
}: {
  removeBoilerMaintenance: boolean;
  removeServiceProposal: boolean;
  orderId: string;
  selectedProduct: { electricityProduct?: { productName?: string }; gasProduct?: { productName?: string } } | undefined;
  selectedServices: {
    boilerMaintenance: string | string[];
    serviceProposal: string | string[];
  };
  mutate: ScopedMutator;
  cacheKey: string;
  setValue: UseFormSetValue<OrderManagementFormSchema>;
}): Promise<void> => {
  try {
    const productItems: string[] = [
      selectedProduct?.electricityProduct?.productName,
      selectedProduct?.gasProduct?.productName,
    ].filter((s): s is string => Boolean(s));
    const serviceItems: string[] = [
      !removeBoilerMaintenance && selectedServices.boilerMaintenance?.toString(),
      !removeServiceProposal && selectedServices.serviceProposal?.toString(),
    ].filter((s): s is string => Boolean(s) && s !== 'none');
    const result = await putEnecoBeXapiSiteApiV1OrdersByOrderIdBasket({
      path: { orderId },
      body: {
        basket: {
          items: productItems
            ?.map(pi => ({ technicalProductName: pi, orderProductType: 'Commodity' as OrderProductTypeDto }))
            .concat(
              serviceItems?.map(si => ({
                technicalProductName: si,
                orderProductType: 'NonCommodity' as OrderProductTypeDto,
              })),
            ),
        },
      },
    });
    if (result.response.ok) {
      mutate(cacheKey);
      if (removeBoilerMaintenance) setValue('boilerMaintenance', '');
      if (removeServiceProposal) setValue('serviceProposal', '');
    }
  } catch (error) {
    logger.error(
      'Failed to clear selected service from order basket:',
      error instanceof Error ? error.message : String(error),
    );
    throw error;
  }
};

export const clearOrderManagementState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.error(
      'Failed to clear order management state from localStorage:',
      error instanceof Error ? error.message : String(error),
    );
  }
};
